import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePricingAdminUser';
import { FilterablePayoutTransactionTable } from '@/components/Payouts/FilterablePayoutTransactionTable';
import { PayoutTransactionFilterName } from '@/components/Payouts/PayoutTransactionsFilter';
import { PayoutTransactionTableColumnName } from '@/components/Payouts/PayoutTransactionTable';
import { useGetOrganizationDetailsQuery } from '@/services/organizations';
import { useGetBatchByIdQuery } from '@/services/payouts';
import { Box, Card } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { ProjectTransactionSort, UserPermissions } from 'types';

/**
 * View a single batch of transactions, it is based off the ProjectTransactionsView component, but we may be able to use composition to clean this up
 *
 * @constructor
 */
export const BatchView = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const { data: batch } = useGetBatchByIdQuery(batchId ? { batchId } : skipToken);
  const { data: organization } = useGetOrganizationDetailsQuery(
    batch?.payeeExternalId ? { alias: batch.payeeExternalId } : skipToken,
  );
  const { claims } = useAuth();

  const isPalmettoFinanceReaderOrAdmin = useMemo(() => {
    return (
      isPalmettoFinanceUser(claims) &&
      (claims?.permissions?.includes(UserPermissions.lightReachAccountingAdmin) ||
        claims?.permissions?.includes(UserPermissions.lightReachAccountingReader))
    );
  }, [claims]);
  const hideColumns = useMemo(() => {
    const columns: PayoutTransactionTableColumnName[] = [
      'batch.batchDate',
      'projectExternalId',
      'pauseExpirationDate',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      columns.push('tags');
      columns.push('payee.name');
    }
    return columns;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const hideFilters = useMemo(() => {
    const columns: PayoutTransactionFilterName[] = [
      'eventDate',
      'payoutHold',
      'projectExternalId',
      'batchDate',
      'pauseExpirationDate',
      'pausedDueToPaymentPlan',
      'payeeType',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      columns.push('isManual');
      columns.push('clawbackRuleName');
      columns.push('payeeName');
    }
    return columns;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const hideSort = useMemo(() => {
    const sorts: ProjectTransactionSort[] = [
      'pausedExpirationDateAsc',
      'pausedExpirationDateDesc',
      'batchDateAsc',
      'batchDateDesc',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      sorts.push('payeeNameAsc', 'payeeNameDesc');
    }
    return sorts;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const batchDateDisplay = useMemo(() => {
    return batch?.batchDate ? DateTime.fromISO(batch.batchDate).toFormat('EEEE MMM d, yyyy') : '';
  }, [batch?.batchDate]);

  return (
    <>
      <Helmet>
        <title>Payouts</title>
      </Helmet>

      <Card padding="sm" marginTop="0">
        <Card.Header>
          <Box direction="row" justifyContent="space-between">
            <Box fontSize="xl" fontWeight="medium">
              Batch - {batchDateDisplay} - {organization?.name}
            </Box>
          </Box>
        </Card.Header>
        <Box margin="0 lg 0 lg">
          <FilterablePayoutTransactionTable
            lockedQueryFilters={{ batchId }}
            enabledButtons={['export']}
            hideColumns={hideColumns}
            hideFilters={hideFilters}
            showPagination
            hideSort={hideSort}
          />
        </Box>
      </Card>
    </>
  );
};
